import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const PayBillModal = ({ isOpen, onClose, bill, onPaymentProcessed }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    amount_paid: '',
    payment_date: new Date()?.toISOString()?.split('T')?.[0],
    payment_method: 'bank_transfer',
    description: '',
    notes: '',
    reference_number: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  // Reset form when modal opens with bill data
  useEffect(() => {
    if (isOpen && bill) {
      const remainingBalance = bill?.balance_due || bill?.total_amount || 0;
      setFormData({
        amount_paid: remainingBalance?.toString(),
        payment_date: new Date()?.toISOString()?.split('T')?.[0],
        payment_method: 'bank_transfer',
        description: `Payment for bill ${bill?.bill_number}`,
        notes: '',
        reference_number: ''
      });
      setError('');
    }
  }, [isOpen, bill]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user types
    if (error) {
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!bill?.id) {
      setError('Bill information is missing. Please try again.');
      return;
    }
    
    // Validation
    const amountPaid = parseFloat(formData?.amount_paid);
    const maxAmount = bill?.balance_due || bill?.total_amount || 0;
    
    if (isNaN(amountPaid) || amountPaid <= 0) {
      setError('Please enter a valid payment amount greater than 0.');
      return;
    }
    
    if (amountPaid > maxAmount) {
      setError(`Payment amount cannot exceed the remaining balance of $${maxAmount?.toLocaleString()}.`);
      return;
    }
    
    if (!formData?.payment_method) {
      setError('Please select a payment method.');
      return;
    }
    
    if (!formData?.payment_date) {
      setError('Please select a payment date.');
      return;
    }
    
    setIsProcessing(true);
    setError('');
    
    try {
      // Prepare payment data using the correct schema field names
      const paymentData = {
        vendor_id: bill?.vendor_id,
        bill_id: bill?.id,
        amount_paid: amountPaid,
        payment_date: formData?.payment_date,
        payment_method: formData?.payment_method,
        description: formData?.description || `Payment for bill ${bill?.bill_number}`,
        notes: formData?.notes || null,
        reference_number: formData?.reference_number || null,
        status: 'cleared', // Mark as cleared by default
        currency_code: bill?.currency_code || 'USD'
      };

      console.log('Processing bill payment:', paymentData);
      
      // Call parent component to handle the payment processing
      await onPaymentProcessed(paymentData);
      
      // Reset form and close modal
      setFormData({
        amount_paid: '',
        payment_date: new Date()?.toISOString()?.split('T')?.[0],
        payment_method: 'bank_transfer',
        description: '',
        notes: '',
        reference_number: ''
      });
      
      onClose();
      
    } catch (err) {
      console.error('Payment processing error:', err);
      setError(err?.message || 'Failed to process payment. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  const remainingBalance = bill?.balance_due || bill?.total_amount || 0;
  const isPartialPayment = parseFloat(formData?.amount_paid) < remainingBalance;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background border border-border rounded-lg shadow-lg w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">Pay Bill</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={isProcessing}
          >
            <Icon name="X" size={16} />
          </Button>
        </div>

        {/* Bill Information */}
        <div className="p-6 border-b border-border bg-muted/20">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Bill Number:</span>
              <span className="font-medium">{bill?.bill_number}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Vendor:</span>
              <span className="font-medium">{bill?.vendor?.vendor_name}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Total Amount:</span>
              <span className="font-medium">${(bill?.total_amount || 0)?.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Balance Due:</span>
              <span className="font-semibold text-primary">${remainingBalance?.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Due Date:</span>
              <span className="font-medium">{new Date(bill?.due_date)?.toLocaleDateString()}</span>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} className="text-red-500 flex-shrink-0" />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {/* Payment Amount */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Payment Amount *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={remainingBalance}
                  value={formData?.amount_paid}
                  onChange={(e) => handleInputChange('amount_paid', e?.target?.value)}
                  className="pl-8"
                  placeholder="0.00"
                  required
                  disabled={isProcessing}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Maximum: ${remainingBalance?.toLocaleString()}
              </p>
              {isPartialPayment && parseFloat(formData?.amount_paid) > 0 && (
                <p className="text-xs text-warning mt-1">
                  <Icon name="AlertTriangle" size={12} className="inline mr-1" />
                  Partial payment - remaining balance will be ${(remainingBalance - parseFloat(formData?.amount_paid || 0))?.toLocaleString()}
                </p>
              )}
            </div>

            {/* Payment Date */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Payment Date *
              </label>
              <Input
                type="date"
                value={formData?.payment_date}
                onChange={(e) => handleInputChange('payment_date', e?.target?.value)}
                required
                disabled={isProcessing}
              />
            </div>

            {/* Payment Method */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Payment Method *
              </label>
              <select
                value={formData?.payment_method}
                onChange={(e) => handleInputChange('payment_method', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-background text-foreground"
                required
                disabled={isProcessing}
              >
                <option value="bank_transfer">Bank Transfer</option>
                <option value="check">Check</option>
                <option value="cash">Cash</option>
                <option value="credit_card">Credit Card</option>
                <option value="debit_card">Debit Card</option>
                <option value="online">Online Payment</option>
                <option value="upi">UPI</option>
                <option value="wallet">Digital Wallet</option>
              </select>
            </div>

            {/* Reference Number */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Reference Number
              </label>
              <Input
                type="text"
                value={formData?.reference_number}
                onChange={(e) => handleInputChange('reference_number', e?.target?.value)}
                placeholder="Transaction reference, check number, etc."
                disabled={isProcessing}
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Description
              </label>
              <Input
                type="text"
                value={formData?.description}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
                placeholder="Payment description"
                disabled={isProcessing}
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Notes
              </label>
              <textarea
                value={formData?.notes}
                onChange={(e) => handleInputChange('notes', e?.target?.value)}
                placeholder="Additional notes..."
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-background text-foreground resize-none"
                rows={3}
                disabled={isProcessing}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isProcessing}
              className="min-w-[120px]"
            >
              {isProcessing ? (
                <>
                  <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Icon name="CreditCard" size={16} className="mr-2" />
                  Process Payment
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PayBillModal;